import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { IGeoCoords, Location, MapMarkerService, MapsService, Sublocation } from '@uzurv/clara';
import { isNullOrUndefined } from '@uzurv/core';

@Component({
  selector: 'app-location-manager-map',
  templateUrl: './location-manager-map.component.html',
  styleUrls: ['./location-manager-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MapMarkerService]
})
export class LocationManagerMapComponent implements OnInit, OnChanges {
  @Input() managing = true;
  @Input() mapCenterCoords: IGeoCoords;
  @Input() userLocation: IGeoCoords;
  @Input() offsetLocationMode: boolean;
  @Input() loadedLocation: Location;
  @Input() hoveredLocation: Location | Sublocation;
  @Input() selectedLocation: Location;
  @Input() addingNewLocation: boolean;

  @Output() mapMoved = new EventEmitter<IGeoCoords>();
  @Output() useOffsetLocation = new EventEmitter<boolean>();
  @Output() mapLoaded = new EventEmitter();

  @ViewChild('googleMap', { static: true }) private _googleMap: ElementRef;
  private _eventListener: google.maps.MapsEventListener;
  private _mapMovedListenerFunction = () => {
    const center = this.mapMarkerService.map.getCenter();
    if (!isNullOrUndefined(this.loadedLocation) && !this.offsetLocationMode && !this.addingNewLocation) {
      // Go into offset location mode
      this.mapMarkerService.setMarkerVisible('location', 'offset', true);
      this.useOffsetLocation.emit(true);
    }
    this.mapMoved.emit({
      lat: center.lat(),
      lng: center.lng()
    });
  };
  private _trafficLayer: google.maps.TrafficLayer;
  private _defaultZoom = 17;

  constructor(private mapsService: MapsService, private mapMarkerService: MapMarkerService) {}

  ngOnInit() {
    this.mapMarkerService.map = this.mapsService.buildMap(this._googleMap.nativeElement, this.userLocation, this._defaultZoom);

    this.mapMarkerService.createMarkerGroup('location');
    this.mapMarkerService.createMarkerGroup('sublocations');

    this.mapMarkerService.createMarker('location', 'main', this.mapCenterCoords, 'assets/selected.png');

    this.mapMarkerService.createMarker('location', 'mapCenter', this.mapCenterCoords, 'assets/sub_new.png', 1000);
    this.mapMarkerService.setMarkerVisible('location', 'mapCenter', false);

    this.mapMarkerService.createMarker('location', 'offset', this.mapCenterCoords, 'assets/offset.png', 1000);
    this.mapMarkerService.setMarkerVisible('location', 'offset', false);

    if (!isNullOrUndefined(this.loadedLocation)) {
      this.addSublocationMarkers();
    }

    this._eventListener = this.mapMarkerService.map.addListener('center_changed', this._mapMovedListenerFunction);

    google.maps.event.addListenerOnce(this.mapMarkerService.map, 'idle', () => {
      this.mapLoaded.emit();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // If the user moves the map
    if (
      changes['mapCenterCoords'] &&
      !isNullOrUndefined(changes['mapCenterCoords'].currentValue) &&
      !changes['mapCenterCoords'].firstChange
    ) {
      // Update the center markers
      this.mapMarkerService.updateMarkerPosition('location', 'mapCenter', this.mapCenterCoords);
      this.mapMarkerService.updateMarkerPosition('location', 'offset', this.mapCenterCoords);

      // And if not in new location mode and not already in offset location mode
      if (!this.addingNewLocation && !this.offsetLocationMode && !this.managing) {
        if (this.selectedLocation && !isNullOrUndefined(this.selectedLocation.sublocations)) {
          this.mapMarkerService.updateMarkerIcon(
            'sublocations',
            this.selectedLocation.sublocations[0].id.toString(),
            'assets/sub_unselected.png'
          );
        } else {
          this.mapMarkerService.updateMarkerIcon('location', 'main', 'assets/unselected.png');
        }
      }
    }

    if (changes['userLocation'] && !isNullOrUndefined(changes['userLocation'].currentValue)) {
      this.mapCenterCoords = changes['userLocation'].currentValue;
      if (!isNullOrUndefined(this.mapMarkerService.map)) {
        this.mapMarkerService.map.setCenter(changes['userLocation'].currentValue);
        this.mapMarkerService.map.setZoom(this._defaultZoom);
      }
    }

    // If entering new location mode
    if (changes['addingNewLocation'] && changes['addingNewLocation'].currentValue) {
      this.mapMarkerService.setMarkerVisible('location', 'offset', false);
      this.mapMarkerService.setMarkerVisible('location', 'mapCenter', true);
    }

    // If leaving new location mode
    if (changes['addingNewLocation'] && !changes['addingNewLocation'].firstChange && changes['addingNewLocation'].currentValue !== true) {
      this.mapMarkerService.setMarkerVisible('location', 'mapCenter', false);
    }

    // If leaving offset location mode
    if (changes['offsetLocationMode'] && !changes['offsetLocationMode'].firstChange && !changes['offsetLocationMode'].currentValue) {
      this.mapMarkerService.setMarkerVisible('location', 'offset', false);
    }

    // If receiving a loaded location
    if (changes['loadedLocation'] && !changes['loadedLocation'].firstChange) {
      this.removeSublocationMarkers();
      if (!isNullOrUndefined(changes['loadedLocation'].currentValue)) {
        this.mapMarkerService.updateMarkerPosition('location', 'main', this.mapCenterCoords);
        this.addSublocationMarkers();
      }
    }

    // If entering a hovered state
    if (changes['hoveredLocation'] && !isNullOrUndefined(changes['hoveredLocation'].currentValue)) {
      // and the hovered item is a sublocation
      if (changes['hoveredLocation'].currentValue instanceof Sublocation) {
        // and the hovered sublocation is the selected sublocation
        if (
          this.selectedLocation &&
          this.selectedLocation.sublocations &&
          this.selectedLocation.sublocations[0] === changes['hoveredLocation'].currentValue
        ) {
          this.mapMarkerService.updateMarkerIcon(
            'sublocations',
            changes['hoveredLocation'].currentValue.id.toString(),
            'assets/sub_selected_hovered.png'
          );
        } else {
          this.mapMarkerService.updateMarkerIcon(
            'sublocations',
            changes['hoveredLocation'].currentValue.id.toString(),
            'assets/sub_unselected_hovered.png'
          );
        }
        // otherwise the hovered location was the location
      } else {
        // if offset point mode
        if (this.selectedLocation === null) {
          this.mapMarkerService.updateMarkerIcon('location', 'main', 'assets/unselected_hovered.png');
        } else if (this.selectedLocation && this.selectedLocation.sublocations === null) {
          this.mapMarkerService.updateMarkerIcon('location', 'main', 'assets/selected_hovered.png');
        } else {
          this.mapMarkerService.updateMarkerIcon('location', 'main', 'assets/unselected_hovered.png');
        }
      }
    }

    // If leaving a hovered state
    if (
      changes['hoveredLocation'] &&
      !changes['hoveredLocation'].firstChange &&
      !isNullOrUndefined(changes['hoveredLocation'].currentValue)
    ) {
      // and the hovered item was a sublocation
      if (changes['hoveredLocation'].previousValue instanceof Sublocation) {
        // and it was selected
        if (
          this.selectedLocation &&
          this.selectedLocation.sublocations &&
          this.selectedLocation.sublocations[0] === changes['hoveredLocation'].previousValue
        ) {
          this.mapMarkerService.updateMarkerIcon(
            'sublocations',
            changes['hoveredLocation'].previousValue.id.toString(),
            'assets/sub_selected.png'
          );
        } else {
          this.mapMarkerService.updateMarkerIcon(
            'sublocations',
            changes['hoveredLocation'].previousValue.id.toString(),
            'assets/sub_unselected.png'
          );
        }
        // otherwise the previously hovered location was the location
      } else {
        // if offset point mode
        if (this.selectedLocation === null) {
          this.mapMarkerService.updateMarkerIcon('location', 'main', 'assets/unselected.png');
        } else if (this.selectedLocation && this.selectedLocation.sublocations === null) {
          this.mapMarkerService.updateMarkerIcon('location', 'main', 'assets/selected.png');
        } else {
          this.mapMarkerService.updateMarkerIcon('location', 'main', 'assets/unselected.png');
        }
      }
    }

    // If receiving a selected location event and not for the first time
    if (changes['selectedLocation'] && !changes['selectedLocation'].firstChange && changes['selectedLocation'].currentValue) {
      // don't want to emit for this map move
      this._eventListener.remove();
      this.mapMarkerService.map.setCenter({ lat: this.selectedLocation.latitude, lng: this.selectedLocation.longitude });
      this.mapMarkerService.map.setZoom(this._defaultZoom);
      this._eventListener = this.mapMarkerService.map.addListener('center_changed', this._mapMovedListenerFunction);

      // If the selected event item is a sublocation
      if (changes['selectedLocation'].currentValue.sublocations) {
        this.mapMarkerService.setMarkerVisible('location', 'offset', false);
        this.mapMarkerService.updateMarkerIcon('location', 'main', 'assets/unselected.png');

        // If the previously selected item was a sublocation
        if (changes['selectedLocation'].previousValue && changes['selectedLocation'].previousValue.sublocations) {
          this.mapMarkerService.updateMarkerIcon(
            'sublocations',
            changes['selectedLocation'].previousValue.sublocations[0].id.toString(),
            'assets/sub_unselected.png'
          );
        } else {
          this.mapMarkerService.updateMarkerIcon('location', 'main', 'assets/unselected.png');
        }

        this.mapMarkerService.updateMarkerIcon(
          'sublocations',
          changes['selectedLocation'].currentValue.sublocations[0].id.toString(),
          'assets/sub_selected_hovered.png'
        );
        // Otherwise the selected item is the location
      } else {
        // and if the previously selected item was a sublocation
        if (changes['selectedLocation'].previousValue && changes['selectedLocation'].previousValue.sublocations) {
          this.mapMarkerService.updateMarkerIcon(
            'sublocations',
            changes['selectedLocation'].previousValue.sublocations[0].id.toString(),
            'assets/sub_unselected.png'
          );
        }
        this.mapMarkerService.updateMarkerIcon('location', 'main', 'assets/selected_hovered.png');
      }
    }
  }

  toggleTraffic(event) {
    if (event.checked === true) {
      this._trafficLayer = new google.maps.TrafficLayer();
      this._trafficLayer.setMap(this.mapMarkerService.map);
    } else {
      this._trafficLayer.setMap(null);
    }
  }

  toggleSatellite(event) {
    if (event.checked === true) {
      this.mapMarkerService.map.setMapTypeId(google.maps.MapTypeId.HYBRID);
    } else {
      this.mapMarkerService.map.setMapTypeId(google.maps.MapTypeId.ROADMAP);
    }
  }

  private addSublocationMarkers(): void {
    if (this.loadedLocation.sublocations) {
      this.loadedLocation.sublocations.forEach(sublocation => {
        this.mapMarkerService.createMarker(
          'sublocations',
          sublocation.id.toString(),
          { lat: sublocation.latitude, lng: sublocation.longitude },
          'assets/sub_unselected.png'
        );
      });
    }
  }

  private removeSublocationMarkers(): void {
    this.mapMarkerService.deleteGroup('sublocations');
  }
}
